import React from 'react';
import { Helmet } from "react-helmet";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - DeepSeek AI Extension | AI Sidebar with Deepseek & AITOPIA</title>
        <meta 
          name="description" 
          content="Privacy Policy for AI Sidebar with Deepseek & AITOPIA Extension. Learn how we collect, use, and protect your data when using our AI browser extension." 
        />
        <meta name="robots" content="index, follow" />
        <meta name="google" content="nositelinksearch" />
        <meta name="google" content="nopagereadaloud" />
        <meta name="google-adsense-account" content="" />
        <link rel="canonical" href="https://deepseek.ai/privacy-policy" />
        
        {/* Block AdSense on this page */}
        <script>{`
          // Block AdSense initialization
          window.adsbygoogle = [];
          window.googletag = { cmd: [] };
          
          // Override AdSense functions
          if (typeof window.adsbygoogle !== 'undefined') {
            window.adsbygoogle.loaded = true;
          }
          
          // Remove any existing ad containers
          document.addEventListener('DOMContentLoaded', function() {
            const ads = document.querySelectorAll('.adsbygoogle, ins[class*="adsbygoogle"], [data-ad-client]');
            ads.forEach(function(ad) {
              ad.remove();
            });
            
            // Continue removing ads as they appear
            const observer = new MutationObserver(function(mutations) {
              mutations.forEach(function(mutation) {
                mutation.addedNodes.forEach(function(node) {
                  if (node.nodeType === 1) {
                    if (node.classList && (node.classList.contains('adsbygoogle') || node.hasAttribute('data-ad-client'))) {
                      node.remove();
                    }
                    const childAds = node.querySelectorAll && node.querySelectorAll('.adsbygoogle, ins[class*="adsbygoogle"], [data-ad-client]');
                    if (childAds) {
                      childAds.forEach(function(ad) { ad.remove(); });
                    }
                  }
                });
              });
            });
            observer.observe(document.body, { childList: true, subtree: true });
          });
        `}</script>
        
        <style>{`
          .adsbygoogle, 
          ins.adsbygoogle, 
          [data-ad-client],
          [class*="google"],
          [id*="google"],
          [class*="ads"],
          [id*="ads"] { 
            display: none !important; 
            visibility: hidden !important;
            opacity: 0 !important;
            width: 0 !important;
            height: 0 !important;
            overflow: hidden !important;
          }
        `}</style>
      </Helmet>

      <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>Disclaimer:</strong> Deepseek.ai is an independent website and is not affiliated with, sponsored by, or endorsed by Hangzhou DeepSeek Artificial Intelligence Co., Ltd.
              </p>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy for AI Sidebar with Deepseek & AITOPIA Extension</h1>
            
            <p className="text-gray-600 mb-8">
              <strong>Last updated:</strong> June 20, 2025
            </p>

            <p className="text-gray-700 mb-8 leading-relaxed">
              Thank you for using AI Sidebar with Deepseek & AITOPIA Extension ("we", "our", or "us"). We are committed to protecting your privacy. This Privacy Policy explains how your information is collected, used, and shared when you install or use our browser extension and associated services.
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Who We Are</h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                This extension integrates two components:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>AI Sidebar with Deepseek, developed by Deepseek.ai.</li>
                <li>AITOPIA, a service provided by AITOPIA Inc.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Both components are responsible for parts of the data processing.
              </p>
            </section>

            <section className="mb-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Anonymous Data</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                We collect and process certain data, including clickstream and query data, in a fully anonymized manner. This data is not traceable to any individual, even when combined with additional information. As such, it falls outside the scope of the GDPR or any similar privacy regulations.
              </p>
              <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-400">
                <p className="text-blue-800 font-medium mb-2">➡️ This means that:</p>
                <ul className="list-disc pl-6 text-blue-700">
                  <li>This anonymized data is not considered personal data</li>
                  <li>Users have no right to access, correct, transfer, or delete this specific dataset</li>
                  <li>This data may be freely used, shared, and analyzed, provided it is done transparently as described here</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. What Data We Collect</h2>
              
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">a. Clickstream Data</h3>
                <p className="text-gray-700 leading-relaxed">
                  We collect anonymized data about the websites you visit, the order of visits, timestamps, and referrers. This helps us analyze trends and improve user experience. All clickstream data is anonymized and cannot be traced back to an individual.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">b. Query Data</h3>
                <p className="text-gray-700 leading-relaxed">
                  When users interact with any AI features (e.g., asking a question), we may store the query text to improve the AI model and analyze usage. These records are de-identified and not linked to personal accounts.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">c. Personal Data (AITOPIA)</h3>
                <p className="text-gray-700 mb-3">If you create an account with AITOPIA, we may collect:</p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Email address</li>
                  <li>Name (optional)</li>
                  <li>Login method (e.g., phone, OAuth)</li>
                  <li>Saved chat history (if enabled)</li>
                  <li>Uploaded files (e.g., HTML, PDF)</li>
                  <li>Browser version and system diagnostics</li>
                </ul>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">d. Usage Data</h3>
                <p className="text-gray-700 mb-3">Automatically collected data includes:</p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Device type and OS</li>
                  <li>Browser type/version</li>
                  <li>Page visit timestamps and durations</li>
                  <li>Interaction logs with the extension interface</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Data</h2>
              <p className="text-gray-700 mb-3">We use your data to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide and improve our services</li>
                <li>Analyze usage trends</li>
                <li>Support account registration and login (AITOPIA)</li>
                <li>Personalize user experience</li>
                <li>Communicate updates and notifications (when opted-in)</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                We may share anonymized, aggregated statistics with partners and improve services with analytics tools (e.g., Google Analytics). We do not sell your personal data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Storage and Hosting</h2>
              <p className="text-gray-700 leading-relaxed">
                All data collected through AITOPIA is stored on Amazon Web Services (AWS) servers located in the United States. We use encrypted transmission (HTTPS), backups, and security protocols such as firewalls.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Sharing</h2>
              <p className="text-gray-700 mb-3">We may share data:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>With analytics providers (e.g., Google Analytics)</li>
                <li>With service providers (e.g., cloud hosting)</li>
                <li>With AI model providers (e.g., when sending your query to an AI system)</li>
                <li>If required by law or legal process</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Chat data and uploaded files are never sold, and access is strictly limited.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. User Rights (GDPR & CCPA)</h2>
              <p className="text-gray-700 mb-3">You have the right to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Access or request your data</li>
                <li>Request data correction or deletion</li>
                <li>Object to data processing</li>
                <li>Export your data (data portability)</li>
                <li>Opt-out of sale of personal data (CCPA)</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                To exercise your rights, contact us at: <a href="mailto:info@deepseek.ai" className="text-blue-600 hover:text-blue-800">info@deepseek.ai</a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                This extension and related services are not intended for users under 13. We do not knowingly collect personal data from children. If you believe a child has provided us personal data, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies and Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                Some features may use cookies or third-party integrations (e.g., YouTube, Analytics). Please refer to your browser settings to manage cookies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Retention of Data</h2>
              <p className="text-gray-700 leading-relaxed">
                Personal data is retained only as long as necessary for the purposes described above. You may delete your account and associated data at any time via the AITOPIA interface or by contacting us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We apply commercially reasonable measures to protect your data. However, no online system can be 100% secure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. Changes take effect when posted on this page.
              </p>
            </section>

            <section className="mb-8 p-4 bg-gray-50 rounded-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed">
                For any questions about this policy, contact us: <a href="mailto:info@deepseek.ai" className="text-blue-600 hover:text-blue-800">info@deepseek.ai</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default PrivacyPolicy;